/**
 * The lobby (architecture §1.1 minute −3 to 0; ADR-076): the campaign list a player
 * picks from, and the shape of the request that turns a pick into a session.
 *
 * A campaign is a pre-authored pack — cast, lore, quests, storylets and openings
 * written before session one — and the card is what the pack says about itself,
 * never a summary a model wrote. Session zero's checklist belongs here rather than
 * on the first message of a session that already exists: a line drawn at creation
 * is in the settings before the opening is chosen, so an opening a line excludes is
 * never offered (ADR-076 honours the lines first).
 */
import { z } from 'zod';
import { CampaignCharacterRules, CharacterBuild } from './builder.js';
import { LineTag } from './lines.js';
import { Locale, Measures } from './primitives.js';
import { NarrationProfile, SessionLength, TimeSkips, SessionState, StorytellerPreset, VoiceSetting } from './session.js';
import { TtsModelId } from './voice.js';
/** The spoken-word band a persona's style contract binds prose to (ADR-080). */
export const WordBand = z
    .object({ min: z.number().int().min(40).max(200), max: z.number().int().min(40).max(200) })
    .refine((band) => band.min < band.max, { message: 'min must be below max' });
/**
 * One game master persona as the lobby shows it (ADR-080). The prose the model is
 * given is not here — the card is what the player reads, and it is authored.
 */
export const PersonaCard = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    blurb: z.string(),
    /** §3.5's director preset this persona paces with. */
    storyteller: StorytellerPreset,
    wordBand: WordBand,
    /** The voice, as `voice_state` will name it: the TTS model and its narrator id. */
    voice: z.object({ model: TtsModelId, narratorId: z.string().min(1) }),
});
/** One campaign as the lobby shows it. Everything here comes from the compiled bundle. */
export const CampaignCard = z.object({
    campaignId: z.string().uuid(),
    packId: z.string(),
    packVersion: z.string(),
    /** The bundle the session will be played against (§9.1); the header records the same value. */
    bundleHash: z.string(),
    title: z.string().min(1),
    /** What the player reads when choosing; authored in the pack, never generated. */
    blurb: z.string(),
    locale: Locale,
    /** How many authored openings the pack has (ADR-076). */
    startingPoints: z.number().int().nonnegative(),
    storylets: z.number().int().nonnegative(),
    /**
     * The line tags any storylet in this pack carries. A drawn line removes exactly
     * the storylets carrying it, so this is what the checklist can actually change
     * for this campaign — a tag no storylet carries is honoured and removes nothing.
     */
    lineTagsInPack: z.array(LineTag),
    /** The authored openings a player may choose from, in authored order; one means no choice to make. */
    openings: z.array(z.object({ id: z.string(), label: z.string(), sessionNumber: z.number().int().min(1).optional() })).default([]),
    /** How many sessions the pack expects this campaign to take, when it says. */
    expectedSessions: z.number().int().min(1).optional(),
    /** ADR-090: what the campaign admits as a character — mode, level, lists and budget. */
    character: CampaignCharacterRules.default({}),
    /** ADR-112: the campaign's first session can open on a prologue; the lobby offers to hear it or not. */
    prologue: z.boolean().default(false),
});
/** `POST /sessions`: what the lobby is allowed to decide. */
export const CreateSessionBody = z.object({
    sessionId: z.string().uuid().optional(),
    campaignId: z.string().uuid().optional(),
    locale: Locale.optional(),
    /** ADR-119: feet or metres for everything the table is shown; the language's own when absent. */
    measures: Measures.optional(),
    profile: NarrationProfile.optional(),
    /** Session zero's lines, in the settings before the opening is chosen. */
    hardExclusions: z.array(LineTag).default([]),
    /** ADR-076: open at this authored starting point rather than the selector's choice. */
    startingPointId: z.string().optional(),
    /** ADR-080: the game master persona; absent means the pack's own. */
    personaId: z.string().optional(),
    /** A one-time pass, required on a gated pod (controlled playtests). */
    passToken: z.string().optional(),
    /** ADR-083: `off` is text mode by choice; the pod sends the voice node nothing for the session. */
    voice: VoiceSetting.optional(),
    /** ADR-087: the sitting's length; the skeleton and the game master's prep scale to it. */
    lengthMinutes: SessionLength.optional(),
    /** ADR-088: pre-determined time-skip policy for the campaign. */
    timeSkips: TimeSkips.optional(),
    /** ADR-088: a stable, client-held key so a campaign can be continued from where its last session ended. */
    playerKey: z.string().min(8).max(80).optional(),
    /** ADR-088: open at the exact moment the last session of this campaign ended for this player key. */
    continue: z.boolean().optional(),
    /** ADR-090: the player's own character, resolved by the pod against the campaign's rules. */
    character: CharacterBuild.optional(),
    /** ADR-090: one of the campaign's ready-made characters instead; absent means the first. */
    pregenId: z.string().min(1).optional(),
    /** ADR-112: `false` opens session one without the prologue — a player who has heard it — straight into the narrator's first scene. */
    prologue: z.boolean().optional(),
});
/** `201` from `POST /sessions`. */
export const SessionCreated = z.object({
    sessionId: z.string().uuid(),
    campaignId: z.string().uuid(),
    state: SessionState,
    /** §5.4: sha256(sessionSeed), published in the lobby before any dice exist. */
    seedCommitment: z.string(),
    /** The WebSocket path for this session's client protocol, relative to the pod. */
    ws: z.string(),
    /** The lines in force from the first turn — what was asked for, de-duplicated. */
    hardExclusions: z.array(z.string()),
    personaId: z.string().optional(),
    /** ADR-083: what the session was opened with; `off` means the client opens no voice leg. A pod
     *  older than the setting answers without it, which is `on`. */
    voice: VoiceSetting.default('on'),
    lengthMinutes: SessionLength.default(60),
    /** ADR-088: which sitting of the campaign this is, and whether it opened from the last one's final moment. */
    sessionNumber: z.number().int().min(1).default(1),
    continued: z.boolean().default(false),
    /** ADR-090: who the player is playing, as the pod resolved it. */
    characterName: z.string().optional(),
});
/** ADR-088: what the lobby shows for "continue where you left off". */
export const ContinuationCard = z.object({
    found: z.boolean(),
    sessionNumber: z.number().int().min(1).optional(),
    locationLabel: z.string().optional(),
    period: z.string().optional(),
    day: z.number().int().optional(),
    endedAtMs: z.number().int().optional(),
    lastLine: z.string().optional(),
    midFight: z.boolean().optional(),
    /** The character died; the campaign is over and cannot be continued. */
    dead: z.boolean().optional(),
});
//# sourceMappingURL=lobby.js.map